<template>
	<div class="onboarding">
		<div
			class="onboarding__bg"
			aria-hidden="true"
		/>

		<div
			class="onboarding__card"
			:class="{ 'onboarding__card--wide': currentStep === StepE.Role || currentStep === StepE.Industry }"
		>
			<img
				:src="logoUrl"
				alt="Signaloid"
				class="onboarding__logo"
			/>

			<transition
				name="onboarding-fade"
				mode="out-in"
				@after-enter="focusStepHeading"
			>
				<div
					v-if="currentStep === StepE.Welcome"
					:key="StepE.Welcome"
					class="onboarding__screen"
				>
					<div class="onboarding__text">
						<h1 class="onboarding__title sgnl-h6-medium sgnl-text-color-default">
							Welcome to the Signaloid Cloud Developer Platform
						</h1>
						<p class="onboarding__body sgnl-paragraph-s-regular sgnl-text-color-secondary">
							The Signaloid Cloud Developer Platform is a development environment for running compute
							tasks on
							<strong>Signaloid uncertainty-aware processors</strong>.
						</p>
						<p class="onboarding__body sgnl-paragraph-s-regular sgnl-text-color-secondary">
							Use the platform to build applications that run compute tasks on the
							<strong>Signaloid Cloud Compute Engine using REST API</strong>.
						</p>
					</div>
					<div class="onboarding__actions">
						<v-btn
							variant="flat"
							color="primary"
							@click="goToRole"
						>
							Get started
							<ArrowRight16 />
						</v-btn>
					</div>
				</div>

				<OnboardingOptionPicker
					v-else-if="currentStep === StepE.Role"
					:key="StepE.Role"
					stepLabel="Step 1 of 2"
					title="What best describes your role?"
					subtitle="We will tailor your workspace and examples to what you do."
					:options="roleOptions"
					:modelValue="onboardingStore.role"
					@update:modelValue="onboardingStore.setRole($event)"
					@continue="goToIndustry"
					@skip="goToIndustry"
				/>

				<OnboardingOptionPicker
					v-else-if="currentStep === StepE.Industry"
					:key="StepE.Industry"
					stepLabel="Step 2 of 2"
					title="Which industry are you in?"
					subtitle="This helps us suggest relevant cores, data sources, and templates."
					:options="industryOptions"
					:columns="3"
					:modelValue="onboardingStore.industry"
					@update:modelValue="onboardingStore.setIndustry($event)"
					@continue="finishQuestions"
					@skip="finishQuestions"
				/>

				<div
					v-else-if="currentStep === StepE.Provisioning"
					:key="StepE.Provisioning"
					class="onboarding__screen onboarding__loader"
					role="status"
					aria-live="polite"
				>
					<template v-if="onboardingStore.accountInitFailed">
						<h1 class="onboarding__title sgnl-h6-medium sgnl-text-color-default">Something went wrong</h1>
						<p class="onboarding__body sgnl-paragraph-s-regular sgnl-text-color-secondary">
							A network error occurred while setting up your account. Please try again in a moment.
							{{ util.ContactSignaloidSupportMessage }}
						</p>
						<div class="onboarding__actions onboarding__actions--center">
							<v-btn
								variant="flat"
								color="primary"
								@click="retryInitialization"
							>
								Retry
							</v-btn>
						</div>
					</template>
					<template v-else>
						<div
							class="onboarding__spinner"
							aria-hidden="true"
						/>
						<h1 class="onboarding__title sgnl-h6-medium sgnl-text-color-default">
							{{ provisioningMessage }}
						</h1>
						<p class="onboarding__body sgnl-paragraph-s-regular sgnl-text-color-secondary">
							Setting up your workspace and compute access. This only takes a moment.
						</p>
					</template>
				</div>

				<div
					v-else-if="currentStep === StepE.AccountReady"
					:key="StepE.AccountReady"
					class="onboarding__screen"
				>
					<div class="onboarding__text">
						<h1 class="onboarding__title sgnl-h6-medium sgnl-text-color-default">
							{{ accountReadyTitle }}
						</h1>
						<p
							v-if="isInvitation"
							class="onboarding__body sgnl-paragraph-s-regular sgnl-text-color-secondary"
						>
							Your account is ready.
						</p>
						<p
							v-else-if="isFreeTier"
							class="onboarding__body sgnl-paragraph-s-regular sgnl-text-color-secondary"
						>
							Your free workspace is ready.
						</p>
						<p
							v-else-if="trialTierDetails"
							class="onboarding__body sgnl-paragraph-s-regular sgnl-text-color-secondary"
						>
							Enjoy {{ util.numberLessThanTenToWords(trialDurationMonths) }} months of free access to the
							{{ trialTierDetails.fullTitle }}.
						</p>
					</div>

					<div
						v-if="trialTierDetails"
						class="onboarding__features"
					>
						<div class="onboarding__features-title sgnl-paragraph-l-medium sgnl-text-color-default">
							You now have access to the following features:
						</div>
						<TierHighlightsList :tierHighlightsToDisplay="tierHighlightsToDisplay" />
					</div>

					<p
						v-if="!isInvitation && !isFreeTier && trialTierDetails"
						class="onboarding__body sgnl-paragraph-s-regular sgnl-text-color-secondary"
					>
						After the trial period, you can keep using the Free Tier features or subscribe to one of the
						paid tiers.
					</p>

					<div class="onboarding__actions">
						<v-btn
							variant="flat"
							color="primary"
							@click="continueToPlatform"
						>
							Continue to dashboard
							<ArrowRight16 />
						</v-btn>
					</div>
				</div>
			</transition>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, markRaw, type Component } from "vue";
import { mapState } from "pinia";

// Types
import { ObjectValues } from "@/types/utilities";
import {
	UserIndustryE,
	UserRoleE,
	USER_INDUSTRY_LABELS,
	USER_ROLE_LABELS,
	type UserIndustry,
	type UserRole,
} from "@/types/user";

// Stores
import { useOnboardingStore } from "@/stores/onboarding";
import { useUserStore } from "@/stores/user";

// Tier config
import { AccountTierDetail, groupCode, TierFeatureHighlight } from "@/js/tierconfig";

// Utilities
import * as util from "@/js/utilities";

// Components
import OnboardingOptionPicker, { type OnboardingOption } from "@/components/WelcomeFlow/OnboardingOptionPicker.vue";
import TierHighlightsList from "@/components/Common/TierHighlightsList.vue";

// Carbon Icons
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";
import Enterprise20 from "@carbon/icons-vue/es/enterprise/20";
import Wallet20 from "@carbon/icons-vue/es/wallet/20";
import Code20 from "@carbon/icons-vue/es/code/20";
import OverflowMenuHorizontal20 from "@carbon/icons-vue/es/overflow-menu--horizontal/20";
import Analytics20 from "@carbon/icons-vue/es/analytics/20";
import Finance20 from "@carbon/icons-vue/es/finance/20";
import Ruler20 from "@carbon/icons-vue/es/ruler/20";
import Bot20 from "@carbon/icons-vue/es/bot/20";
import Gears20 from "@carbon/icons-vue/es/gears/20";
import MachineLearningModel20 from "@carbon/icons-vue/es/machine-learning-model/20";
import DeliveryTruck20 from "@carbon/icons-vue/es/delivery-truck/20";
import Chemistry20 from "@carbon/icons-vue/es/chemistry/20";

// Assets
import logoUrl from "@/assets/images/signaloid-logo-v3.0-black-green-xHeight8.5pxPadding-200.png";

const StepE = {
	Welcome: "Welcome",
	Role: "Role",
	Industry: "Industry",
	Provisioning: "Provisioning",
	AccountReady: "AccountReady",
} as const;
type Step = ObjectValues<typeof StepE>;

// Default trial for the account-ready screen (the /welcome route passes no props).
const TRIAL_TIER_CODE = "pro-tier";
const TRIAL_DURATION_MONTHS = 2;

const PROVISIONING_INITIAL_MESSAGE = "Creating your dashboard…";
const PROVISIONING_FOLLOWUP_MESSAGE = "Almost there…";
const PROVISIONING_MESSAGE_DELAY_MS = 3000;

// Ids and labels come from types/user.ts; the icons are picker chrome, so only
// the pairing lives here.
const ROLE_ICONS: Record<UserRole, Component> = {
	[UserRoleE.CxO]: markRaw(Enterprise20),
	[UserRoleE.Finance]: markRaw(Wallet20),
	[UserRoleE.Engineering]: markRaw(Code20),
	[UserRoleE.Other]: markRaw(OverflowMenuHorizontal20),
};

const INDUSTRY_ICONS: Record<UserIndustry, Component> = {
	[UserIndustryE.QuantitativeFinance]: markRaw(Analytics20),
	[UserIndustryE.DigitalBanking]: markRaw(Finance20),
	[UserIndustryE.EngineeringDesign]: markRaw(Ruler20),
	[UserIndustryE.Robotics]: markRaw(Bot20),
	[UserIndustryE.IndustrialAutomation]: markRaw(Gears20),
	[UserIndustryE.MachineLearningAI]: markRaw(MachineLearningModel20),
	[UserIndustryE.SupplyChainPlanning]: markRaw(DeliveryTruck20),
	[UserIndustryE.HybridQuantumComputing]: markRaw(Chemistry20),
	[UserIndustryE.Other]: markRaw(OverflowMenuHorizontal20),
};

const ROLE_OPTIONS: OnboardingOption[] = Object.values(UserRoleE).map((id) => ({
	id,
	label: USER_ROLE_LABELS[id],
	icon: ROLE_ICONS[id],
}));

const INDUSTRY_OPTIONS: OnboardingOption[] = Object.values(UserIndustryE).map((id) => ({
	id,
	label: USER_INDUSTRY_LABELS[id],
	icon: INDUSTRY_ICONS[id],
}));

type ComponentData = {
	currentStep: Step;
	sawProvisioning: boolean;
	provisioningMessage: string;
	provisioningTimer: ReturnType<typeof setTimeout> | undefined;
	provisioningEnteredAt: number | undefined;
	previousOverflow: string | undefined;
};

export default defineComponent({
	name: "WelcomePage",
	components: { OnboardingOptionPicker, TierHighlightsList, ArrowRight16 },
	setup() {
		const onboardingStore = useOnboardingStore();
		const userStore = useUserStore();
		return {
			onboardingStore,
			userStore,
			util,
			logoUrl,
			StepE,
			roleOptions: ROLE_OPTIONS,
			industryOptions: INDUSTRY_OPTIONS,
			trialTierCode: TRIAL_TIER_CODE,
			trialDurationMonths: TRIAL_DURATION_MONTHS,
		};
	},
	data: (): ComponentData => ({
		currentStep: StepE.Welcome,
		sawProvisioning: false,
		provisioningMessage: PROVISIONING_INITIAL_MESSAGE,
		provisioningTimer: undefined,
		provisioningEnteredAt: undefined,
		previousOverflow: undefined,
	}),
	computed: {
		...mapState(useOnboardingStore, ["accountReady", "accountInitFailed"]),
		isInvitation(): boolean {
			return !!this.onboardingStore.invitation;
		},
		isFreeTier(): boolean {
			return !this.isInvitation && this.onboardingStore.subscribedTier === "FreeTier";
		},
		trialTierDetails(): AccountTierDetail | undefined {
			const tierDetails = this.userStore.tierDetails;
			const invitationTier = this.onboardingStore.invitation?.Tier;
			if (invitationTier) {
				const code = groupCode(invitationTier);
				if (code) return tierDetails.find((e) => e.code === code);
			}
			const subscribedTier = this.onboardingStore.subscribedTier;
			if (subscribedTier) {
				const code = groupCode(subscribedTier);
				if (code) return tierDetails.find((e) => e.code === code);
			}
			return tierDetails.find((e) => e.code === this.trialTierCode);
		},
		tierHighlightsToDisplay(): TierFeatureHighlight[] {
			return this.trialTierDetails?.shortVersionPoints ?? [];
		},
		accountReadyTitle(): string {
			const orgName = this.onboardingStore.invitation?.OrganizationName;
			return orgName ? `Welcome to ${orgName}!` : "Your account is ready!";
		},
	},
	watch: {
		// Init finished while on the provisioning screen, so record the dwell and advance.
		accountReady: {
			immediate: true,
			handler(ready: boolean) {
				if (ready) {
					void this.userStore.fetchTierCatalog();
				}
				if (ready && this.currentStep === StepE.Provisioning) {
					this.clearProvisioningTimer();
					this.dismissProvisioning("ready");
					this.currentStep = StepE.AccountReady;
				}
			},
		},
		// Init failed while on the provisioning screen, so stop the timer and record the dwell.
		accountInitFailed(failed: boolean) {
			if (failed && this.currentStep === StepE.Provisioning) {
				this.clearProvisioningTimer();
				this.dismissProvisioning("failed");
			}
		},
		// Step-view funnel event. saw_provisioning_loader records whether init outlasted
		// the questions and the user saw the loading screen.
		currentStep(step: Step) {
			if (step === StepE.Provisioning) {
				this.sawProvisioning = true;
			}
			// @ts-ignore posthog is injected globally
			this.$posthog?.capture("onboarding_step_viewed", {
				// Named distinctly from the numeric `step` other events emit, so exact-match filters work.
				onboarding_step: step,
				saw_provisioning_loader: this.sawProvisioning,
			});
		},
	},
	methods: {
		goToRole() {
			this.currentStep = StepE.Role;
		},
		goToIndustry() {
			this.currentStep = StepE.Industry;
		},
		finishQuestions() {
			// Persist the answers without blocking the flow.
			void this.onboardingStore.submitProfileAnswers();

			if (this.onboardingStore.accountReady) {
				this.currentStep = StepE.AccountReady;
				return;
			}
			// Init is still running, or has already failed: show the provisioning screen.
			// Start the spinner timer only when init is still running; a failure renders the error at once.
			this.currentStep = StepE.Provisioning;
			if (!this.onboardingStore.accountInitFailed) {
				this.startProvisioningMessages();
			}
		},
		startProvisioningMessages() {
			this.provisioningMessage = PROVISIONING_INITIAL_MESSAGE;
			this.provisioningEnteredAt = performance.now();
			this.clearProvisioningTimer();
			this.provisioningTimer = setTimeout(() => {
				this.provisioningMessage = PROVISIONING_FOLLOWUP_MESSAGE;
			}, PROVISIONING_MESSAGE_DELAY_MS);
		},
		clearProvisioningTimer() {
			if (this.provisioningTimer) {
				clearTimeout(this.provisioningTimer);
				this.provisioningTimer = undefined;
			}
		},
		// Records how long the provisioning spinner was visible; emits at most once per show.
		dismissProvisioning(outcome: "ready" | "failed" | "abandoned") {
			if (this.provisioningEnteredAt === undefined) {
				return;
			}
			const visibleMs = Math.round(performance.now() - this.provisioningEnteredAt);
			this.provisioningEnteredAt = undefined;
			// @ts-ignore posthog is injected globally
			this.$posthog?.capture("provisioning_loader_dismissed", {
				visible_ms: visibleMs,
				outcome,
			});
		},
		// After each transition, move focus to the new heading so keyboard and screen-reader focus is not stranded.
		focusStepHeading() {
			const heading = this.$el?.querySelector?.(".onboarding__card h1");
			if (heading instanceof HTMLElement) {
				heading.setAttribute("tabindex", "-1");
				heading.focus({ preventScroll: true });
			}
		},
		retryInitialization() {
			this.startProvisioningMessages();
			void this.onboardingStore.retryAccountInitialization();
		},
		continueToPlatform() {
			// Flush before the hard navigation. Include the provisioning and profile context so the event can be segmented.
			// @ts-ignore posthog is injected globally
			this.$posthog?.capture(
				"onboarding_completed",
				{
					saw_provisioning_loader: this.sawProvisioning,
					role: this.onboardingStore.role,
					industry: this.onboardingStore.industry,
				},
				{ send_instantly: true },
			);
			window.location.href = "/";
		},
	},
	created() {
		// @ts-ignore posthog is injected globally
		this.$posthog?.setPersonProperties({
			"user.account_created": true,
			"user.created_at": new Date().toISOString(),
		});
	},
	mounted() {
		// @ts-ignore posthog is injected globally
		this.$posthog?.capture("onboarding_started");

		// The store is an app singleton that survives route changes, so reset it first,
		// then start account init in the background while the user answers the questions.
		this.onboardingStore.reset();
		void this.onboardingStore.startAccountInitialization();

		// Full-screen overlay: lock background scroll while onboarding is shown.
		this.previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
	},
	beforeUnmount() {
		this.clearProvisioningTimer();
		if (this.currentStep === StepE.Provisioning) {
			this.dismissProvisioning("abandoned");
		}
		document.body.style.overflow = this.previousOverflow ?? "";
		this.previousOverflow = undefined;
	},
});
</script>

<style lang="scss" scoped>
.onboarding {
	position: fixed;
	inset: 0;
	z-index: 2000;
	display: grid;
	place-items: center;
	padding: var(--sgnl-space-48) var(--sgnl-space-24);
	overflow: auto;
	background: var(--sgnl-default-primary-background-mono);
}

@media (max-width: 899px) {
	.onboarding {
		padding: var(--sgnl-space-16);
	}
}

// Layered radial gradients in brand green behind the card.
.onboarding__bg {
	position: absolute;
	inset: 0;
	pointer-events: none;
	opacity: 0.22;
	background:
		radial-gradient(46% 42% at 22% 26%, var(--sgnl-brand-background) 0%, transparent 60%),
		radial-gradient(42% 40% at 82% 74%, var(--sgnl-brand-background) 0%, transparent 58%),
		radial-gradient(64% 58% at 58% 50%, var(--sgnl-brand-background-inverse) 0%, transparent 72%);
}

.onboarding__card {
	position: relative;
	width: min(var(--sgnl-container-560), 100%);
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-20);
}

.onboarding__card--wide {
	width: min(var(--sgnl-container-640), 100%);
}

.onboarding__logo {
	height: var(--sgnl-unit-28);
	width: auto;
	align-self: flex-start;
	display: block;
}

.onboarding__screen {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-24);
}

.onboarding__text {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.onboarding__actions {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-16);
}

.onboarding__actions--center {
	justify-content: center;
}

.onboarding__features {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.onboarding__loader {
	gap: var(--sgnl-space-16);
	align-items: center;
	text-align: center;
}

.onboarding__spinner {
	width: var(--sgnl-icon-48);
	height: var(--sgnl-icon-48);
	border-radius: 50%;
	border: var(--sgnl-border-width-2) solid var(--sgnl-default-tertiary-border);
	border-top-color: var(--sgnl-brand-background);
	/* No motion token covers decorative spin timing. */
	animation: onboarding-spin 0.8s linear infinite;
}

@keyframes onboarding-spin {
	to {
		transform: rotate(360deg);
	}
}

.onboarding-fade-enter-active {
	transition:
		opacity var(--sgnl-motion-duration-moderate-02) var(--sgnl-expressive-effect-entrance),
		transform var(--sgnl-motion-duration-moderate-02) var(--sgnl-expressive-effect-entrance);
}

.onboarding-fade-leave-active {
	transition:
		opacity var(--sgnl-motion-duration-moderate-01) var(--sgnl-productive-effect-exit),
		transform var(--sgnl-motion-duration-moderate-01) var(--sgnl-productive-effect-exit);
}

.onboarding-fade-enter-from {
	opacity: 0;
	transform: translateY(var(--sgnl-space-8));
}

.onboarding-fade-leave-to {
	opacity: 0;
	transform: translateY(calc(-1 * var(--sgnl-space-8)));
}

@media (prefers-reduced-motion: reduce) {
	.onboarding-fade-enter-from,
	.onboarding-fade-leave-to {
		transform: none;
	}
}
</style>
